import * as React from 'react';
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '../ui/sidebar'; // <- path to your copied shadcn components
import { SidebarProvider } from '../ui/sidebar';
import { LayoutDashboard, ImageIcon, List, Monitor, Settings, ChevronRight } from 'lucide-react';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import CollapsedLogo from "@/images/H_BLUE.png";
import ExpandedLogo from "@/images/HUGHES-DIGITAL-SIGNAGE_BLUE.png";
import MssStopSign from '../shared/mss-stop-sign';
import { PlaylistIcon } from '../shared/icons/mss-playlist-icon';

type NavItem = { key: string; label: string; path: string; icon?: string };
type Props = {
  navItems: NavItem[];
  activeKey?: string;
  user?: { name?: string; first_name?: string; email?: string; avatarUrl?: string; isImpersonating: boolean };
};

const iconMap: Record<string, React.ReactNode> = {
  'layout-dashboard': <LayoutDashboard className='h-4 w-4' />,
  image: <ImageIcon className='h-4 w-4' />,
  list: <PlaylistIcon className='h-4 w-4' />,
  monitor: <Monitor className='h-4 w-4' />,
  settings: <Settings className='h-4 w-4' />,
};

function safeInitials(name?: string): string {
  try {
    if (!name || name.trim() === '') {
      return 'U';
    }

    // Split by whitespace, take first character of each word, join them
    const initials = name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join('')
      .slice(0, 2); // limit to first 2 characters

    return initials ? initials.toUpperCase() : 'U';
  } catch {
    return 'U';
  }
}

function footerColor(impersonating: boolean): string {
  return impersonating ? "bg-red-400" : "";
}

function linkColor(impersonating: boolean): string {
  return impersonating ? "bg-red-700" : "bg-zinc-100";
}

export default function MssAppSidebar({ navItems, activeKey, user }: Props) {
  const safe_initials = safeInitials(user.name);
  const [avatarUrl, setAvatarUrl] = React.useState(user?.avatarUrl);

  React.useEffect(() => {
    setAvatarUrl(user?.avatarUrl);
  }, [user?.avatarUrl]);

  React.useEffect(() => {
    const handleAvatarUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ avatarUrl?: string }>;
      if (customEvent.detail?.avatarUrl) {
        setAvatarUrl(customEvent.detail.avatarUrl);
      }
    };

    window.addEventListener("avatar-updated", handleAvatarUpdated);

    return () => {
      window.removeEventListener("avatar-updated", handleAvatarUpdated);
    };
  }, []);

  return (
    // 'w-auto' allows the provider to shrink to fit the collapsed sidebar
    // 'min-h-full' ensures it spans height. This fixes layout overlap issues caused by
    // the sidebar being mounted in a separate container from the main content.
    <SidebarProvider className="w-auto min-h-full">
      {/* collapsible="icon" ensures correct spacer width calculation */}
      <Sidebar collapsible="icon" className='h-screen bg-sidebar border-r border-sidebar-border'>
        <SidebarHeader className="h-14 shrink-0 flex items-center justify-center p-2">
          <img
            src={ExpandedLogo}
            alt="Hughes Digital Signage"
            className="block h-10 w-auto object-contain in-data-[collapsible=icon]:hidden"
          />
          <img
            src={CollapsedLogo}
            alt="Hughes"
            className="hidden h-10 w-auto object-contain in-data-[collapsible=icon]:block"
          />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((n) => (
                  <SidebarMenuItem className="p-1" key={n.key}>
                    <SidebarMenuButton asChild isActive={String(activeKey) === String(n.key)}>
                      <a href={n.path} data-test-id={`sidebar-${n.label.toLowerCase()}-link`}>
                        {n.icon && iconMap[n.icon]}
                        <span className="font-semibold font-['Inter'] text-[16px]">{n.label}</span>
                        {String(activeKey) === String(n.key) && (
                          <ChevronRight className='w-24 h-24 ml-auto shrink-0' />
                        )}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className={footerColor(user.isImpersonating)}>
          <SidebarMenu>
            <SidebarMenuItem>
              <Separator className="mb-2" />

              {/* Row: account link (flex-1) + stop button (auto) */}
              <div className="flex items-center gap-2 w-full">
                {/* Account link (left, takes remaining space) */}
                <a
                  data-test-id="sidebar-user-profile"
                  href="/dash/account"
                  className={`flex items-center gap-2 p-2 rounded-md hover:${linkColor(
                    user.isImpersonating
                  )} transition-colors flex-1 min-w-0`}
                >
                  <Avatar className="w-8 h-8 items-center">
                    <AvatarImage src={avatarUrl} alt="User avatar" />
                    <AvatarFallback>{safe_initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-bold text-zinc-700 truncate">
                      {user.first_name}
                    </p>
                    <p className="text-xs text-zinc-700 truncate">{user.email}</p>
                  </div>
                </a>

                {/* Stop impersonating (right), only when impersonating */}
                {user.isImpersonating && (
                  <form action="/profile/stop_impersonating" method="post" className="shrink-0">
                    <input
                      type="hidden"
                      name="authenticity_token"
                      value={
                        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ||
                        ""
                      }
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center cursor-pointer text-zinc-700 hover:text-red-700 font-bold"
                      title="Stop impersonating"
                      aria-label="Stop impersonating"
                    >
                      <MssStopSign size={36} />
                    </button>
                  </form>
                )}
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}
